import useSearchFilter from "hooks/useSearchFilter";
import React, { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import FilterDropdown from "./FilterDropdown";
import { formatNumericNumber } from "utils/formatNumber";
import { formatFilterText } from "utils/searchFilter";
import FilterButton from "components/Buttons/FilterButton";

const RangeFilter = ({
  id,
  placeholder,
  options,
  unit,
  paramMin,
  paramMax,
  setMinValue,
  setMaxValue,
  minValue,
  maxValue,
  Icon,
}) => {
  const [isMaxFocused, setIsMaxFocused] = useState(false);
  const [displayValue, setDisplayValue] = useState("");
  const firstInputRef = useRef(null);
  const secondInputRef = useRef(null);
  const itemRefs = useRef([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const urlParams = new URLSearchParams(searchParams.toString());
  const { toggleFilterDropdown } = useSearchFilter();

  const handleValueChange = (setter, value, isMin) => {
    if (/^\d*\.?\d*$/.test(value)) {
      setter(value);
      const newValue = formatFilterText(
        isMin ? value : minValue,
        isMin ? maxValue : value,
        unit
      );
      setDisplayValue(newValue);
    }
  };

  const handleMinChange = (e) => {
    handleValueChange(setMinValue, e.target.value, true);
  };

  const handleMaxChange = (e) => {
    handleValueChange(setMaxValue, e.target.value, false);
  };

  const updateUrlParams = (paramKey, value) => {
    if (value && value !== "Indifferente") {
      urlParams.set(paramKey, value);
    } else {
      urlParams.delete(paramKey);
    }
    setSearchParams(urlParams);
  };

  const handleSelect = (val, id) => {
    if (isMaxFocused) {
      if (minValue && val < minValue) {
        updateUrlParams(paramMin, val);
        updateUrlParams(paramMax, minValue);
        setMinValue(val);
        setMaxValue(minValue);
        setDisplayValue(formatFilterText(val, minValue, unit));
      } else {
        updateUrlParams(paramMax, val);
        setMaxValue(val);
        setDisplayValue(formatFilterText(minValue, val, unit));
      }
      toggleFilterDropdown(id);
    } else {
      if (val === "Indifferente") {
        updateUrlParams(paramMin, val);
        setMinValue(val);
        setDisplayValue(formatFilterText(val, maxValue, unit));
      } else if (maxValue && val > maxValue) {
        updateUrlParams(paramMin, maxValue);
        updateUrlParams(paramMax, val);
        setMinValue(maxValue);
        setMaxValue(val);
        setDisplayValue(formatFilterText(maxValue, val, unit));
      } else {
        updateUrlParams(paramMin, val);
        setMinValue(val);
        setDisplayValue(formatFilterText(val, maxValue, unit));
      }
      setIsMaxFocused(true);
      secondInputRef.current.focus();
    }
  };

  const handleBlur = () => {
    if (minValue && maxValue && parseFloat(minValue) > parseFloat(maxValue)) {
      updateUrlParams(paramMin, maxValue);
      updateUrlParams(paramMax, minValue);
      setMinValue(maxValue);
      setMaxValue(minValue);
      setDisplayValue(formatFilterText(maxValue, minValue, unit));
    } else {
      updateUrlParams(paramMin, minValue);
      updateUrlParams(paramMax, maxValue);
    }
  };

  // scroll to option position if input option match with list
  const scrollToMatch = () => {
    const currentVal = isMaxFocused ? maxValue : minValue;

    if (currentVal) {
      const matchingIndex = options.findIndex(
        (val) => parseFloat(val) === parseFloat(currentVal)
      );

      if (matchingIndex !== -1 && itemRefs.current[matchingIndex]) {
        const listItemHeight = itemRefs.current[matchingIndex].offsetHeight;

        const scrollPosition = listItemHeight * matchingIndex;

        const dropdownContainer = document.getElementById(`${id}List`);
        dropdownContainer.scrollTo({
          top: scrollPosition,
          behavior: "auto",
        });
      }
    }
  };

  useEffect(() => {
    scrollToMatch();
  }, [minValue, maxValue, isMaxFocused]);

  useEffect(() => {
    const urlParams = new URLSearchParams(searchParams.toString());
    const initialMin = urlParams.get(paramMin) || "";
    const initialMax = urlParams.get(paramMax) || "";

    setMinValue(initialMin);
    setMaxValue(initialMax);
    setDisplayValue(formatFilterText(initialMin, initialMax, unit));
  }, [searchParams]);

  return (
    <FilterDropdown
      id={id}
      selectedItem={displayValue}
      placeholder={placeholder}
      dropdownStyle={{ width: 320 }}
      firstInputRef={firstInputRef}
      closeHandler={handleBlur}
      Icon={Icon}
    >
      <div className="flex items-center p-4 border-b border-[#d5d7dd] justify-between space-x-4">
        <input
          ref={firstInputRef}
          className="input-arrow-hide outline-none border h-10 rounded-md flex-1 w-full px-2 text-[14px] focus:ring-1 ring-primary"
          placeholder="Da"
          value={minValue}
          onChange={handleMinChange}
          onFocus={() => setIsMaxFocused(false)}
          onBlur={handleBlur}
        />
        <input
          ref={secondInputRef}
          className="input-arrow-hide outline-none border h-10 rounded-md flex-1 w-full px-2 text-[14px] focus:ring-1 ring-primary"
          placeholder="A"
          value={maxValue}
          onChange={handleMaxChange}
          onFocus={() => setIsMaxFocused(true)}
          onBlur={handleBlur}
        />
      </div>
      <div id={`${id}List`} className="max-h-[200px] overflow-y-auto">
        <ul>
          <li
            style={{ textAlign: isMaxFocused ? "right" : "left" }}
            className={`filter-dropdown-list ${
              isMaxFocused
                ? maxValue === "Indifferente"
                  ? "text-primary bg-primary bg-opacity-10"
                  : "text-black bg-white"
                : minValue === "Indifferente"
                ? "text-primary bg-primary bg-opacity-10"
                : "text-black bg-white"
            } ${isMaxFocused ? "text-right" : "text-left"}`}
            onClick={() => handleSelect("Indifferente", id)}
          >
            Indifferente
          </li>
          {options.map((number, index) => {
            const isMatchingMin =
              !isMaxFocused && minValue && parseFloat(minValue) === number;
            const isMatchingMax =
              isMaxFocused && maxValue && parseFloat(maxValue) === number;

            const itemClasses = `filter-dropdown-list ${
              isMatchingMin || isMatchingMax
                ? "text-primary bg-primary bg-opacity-10"
                : "text-black bg-white"
            } ${isMaxFocused ? "text-right" : "text-left"}`;

            return (
              <li
                key={number}
                ref={(el) => (itemRefs.current[index + 1] = el)}
                className={itemClasses}
                onClick={() => handleSelect(number, id)}
              >
                {formatNumericNumber(number)} {unit}
              </li>
            );
          })}
        </ul>
      </div>
      <FilterButton />
    </FilterDropdown>
  );
};

export default RangeFilter;
